
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { CreditCard, Plus, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const CreditDisplay = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);

  // Query for credits
  const { data: credits, refetch, isError } = useQuery({
    queryKey: ['credits'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      console.log('Fetching credits for user:', user.id);

      const { data, error } = await supabase
        .from('user_credits')
        .select('credits, updated_at')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching credits:', error);
        throw error;
      }
      
      console.log('Credits data:', data);
      return data?.credits ?? 0;
    },
  });

  // Query for price information
  const { data: priceInfo } = useQuery({
    queryKey: ['price-info'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { user, action: 'get-price' },
      });

      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    const paymentStatus = searchParams.get('payment_status');
    if (paymentStatus === 'success') {
      toast.success('Payment successful! Credits will be added to your account soon.');
      setTimeout(() => {
        refetch();
      }, 2000);
    } else if (paymentStatus === 'cancelled') {
      toast.error('Payment cancelled.');
    }
  }, [searchParams, refetch]);

  const handleBuyCredits = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { user },
      });
      
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error('Failed to initiate checkout. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatPrice = (amount?: number, currency?: string) => {
    if (!amount || !currency) return '';
    const formatter = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency.toUpperCase(),
    });
    return formatter.format(amount / 100); // Convert cents to pounds
  };

  if (isError) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-md">
        <p className="text-red-500">Error loading credits. Please refresh the page.</p>
      </div>
    );
  }

  if (credits === undefined) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-md flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-sage-600" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-6 shadow-md">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CreditCard className="w-6 h-6 text-sage-600" />
          <div>
            <h3 className="text-lg font-semibold text-sage-800">Available Credits</h3>
            <p className="text-3xl font-bold text-sage-600">{credits}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {priceInfo && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 text-sm text-sage-600">
                    <span>{formatPrice(priceInfo.unit_amount, priceInfo.currency)} per 100 credits</span>
                    <Info className="w-4 h-4" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Purchase credits in bundles of 100 to generate more content</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <Button onClick={handleBuyCredits} disabled={isLoading} className="gap-2">
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Buy Credits
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
